import { useState, useEffect, useRef, useCallback } from "react";

const useProjectEventsSSE = ({ projectId, token, enabled, onEvent }) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);

  const onEventRef = useRef(onEvent);
  const tokenRef = useRef(token);
  const projectIdRef = useRef(projectId);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    projectIdRef.current = projectId;
  }, [projectId]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const handleEvent = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      if (onEventRef.current) onEventRef.current(data);
    } catch (err) {
      // Ignore unparseable events (heartbeat comments)
    }
  }, []);

  const connect = useCallback(() => {
    const token = tokenRef.current;
    const projectId = projectIdRef.current;
    if (!token || !projectId || !enabledRef.current) return;

    cleanup();

    const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
    const sseUrl = `${baseUrl}/projects/${projectId}/events/stream?token=${encodeURIComponent(
      token
    )}`;

    try {
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
      };

      eventSource.onmessage = handleEvent;
      eventSource.addEventListener("notification", handleEvent);

      eventSource.onerror = () => {
        setIsConnected(false);
        eventSource.close();
        eventSourceRef.current = null;
        if (!enabledRef.current) return;

        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttemptRef.current),
          30000
        );
        reconnectAttemptRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };
    } catch (err) {
      console.error("Failed to establish project SSE connection:", err);
    }
  }, [cleanup, handleEvent]);

  useEffect(() => {
    if (enabled && token && projectId) {
      connect();
    } else {
      cleanup();
    }
    return cleanup;
  }, [enabled, token, projectId, connect, cleanup]);

  return { isConnected };
};

export default useProjectEventsSSE;
